'use client';

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ProtoWallet } from '@ducat-unit/client-sdk';
import type { ChainNetwork, ProtoProfile, VaultTerms } from '@ducat-unit/core';
import { get_vault_terms } from '@ducat-unit/core/lib';
import { capitalize } from 'lodash';
import { useTranslations } from 'next-intl';

import { identifyPosthogUserByWalletAddress } from '@/components/layout/cookie-policy/cookie.utils';
import { customToast } from '@/components/Toast';
import { getConfig } from '@/config/config';
import { getFrontendNetwork } from '@/config/deployment';
import { resetWalletQueries } from '@/hooks';
import type { getActiveWalletIdentities } from '@/hooks/api/quanta/auth';
import { resetQuantaQueries } from '@/hooks/api/quanta/quanta.helpers';
import { quantaHeadersBelongToWallet, rememberQuantaIdentities } from '@/hooks/api/quanta/quantaIdentity';
import useLocalStorage from '@/hooks/useLocalStorage';
import { useContractProfile } from '@/hooks/wallet/useContractProfile';
import type { FunctionVoid } from '@/lib/connector';
import { requestSnapConnection } from '@/lib/metamask-snap-connection';
import posthog from '@/lib/posthog/client';
import { getWalletConnector, loadWalletConnector, wait } from '@/lib/util';
import { isObservedWalletNetworkCompatible, type WalletActionGuard } from '@/lib/wallet-action-guard';
import { WalletAccessDeniedError, WalletConnectionPendingError } from '@/lib/wallet-connection-error';
import { clearDefaultWalletProvider } from '@/lib/wallet-connector-loader';

import { useAppStore, useConfig } from '../stores/useAppStore';
import { useNotificationStore } from '../stores/useNotificationStore';

import { configureProtoWallet, connectWallet, createCurrentWalletActionGuard, reconnectWallet } from './helpers';
import { type WalletInfo, WalletType } from './types';
import {
  type AccountPublication,
  createWalletAccountSnapshot,
  type WalletAccountSnapshot,
  walletAccountSnapshotsEqual,
} from './wallet-account';

interface DucatClientProviderProps {
  children: React.ReactNode;
}

interface ClientContext {
  client: DucatClientAPI;
  contract: ProtoProfile | undefined;
  terms: VaultTerms | undefined;
  isContractLoading: boolean;
  contractError: Error | null;
  retryContract: () => Promise<boolean>;
  isConnected: boolean;
  wallet: ProtoWallet | null;
  walletType: WalletType | null;
  walletAddress: string | null;
  pubkey: string | null;
  connectedNetwork: ChainNetwork | 'unknown' | null;
  accountRevision: number;
  createWalletActionGuard: () => WalletActionGuard;
  handleNetworkChange: (network: ChainNetwork) => void;
  // setNetworkSettings: (settings: NetworkSettings) => void;
}

export interface DucatClientAPI {
  connect: (walletType: WalletType) => Promise<boolean>;
  disconnect: VoidFunction;
  // biome-ignore lint/suspicious/noExplicitAny: <TODO: fix any>
  signMessage: (options: SignMessageOptions) => any;
  subscribeToWalletConnect: (callback: VoidFunction) => VoidFunction;
  subscribeToWalletDisconnect: (callback: VoidFunction) => VoidFunction;
}

export interface NetworkSettings {
  network: ChainNetwork | undefined;
  // masterId: string;
  guardianUrl: string;
  guardianPublicKey: string;
  customGuardian: boolean;
}

interface OnConnectWalletAccountParams {
  walletType: WalletType;
  wallet?: ProtoWallet;
  publication: AccountPublication;
  /** Key inside the `notifications` message namespace. */
  errorMessageKey?: string;
}

interface SignMessageOptions {
  address: string;
  message: string;
}

const context = createContext<ClientContext | null>(null);

export const DucatClientProvider = ({ children }: DucatClientProviderProps): React.JSX.Element => {
  const t = useTranslations('notifications');
  const setWalletErrorMessage = useAppStore(({ setWalletErrorMessage }) => setWalletErrorMessage);
  const setResetDrawers = useAppStore(({ setResetDrawers }) => setResetDrawers);
  const setQuantaHeaders = useAppStore(({ setQuantaHeaders }) => setQuantaHeaders);
  const resetActionTx = useAppStore(({ resetActionTx }) => resetActionTx);
  const selectedNetwork = getFrontendNetwork();
  const config = useConfig();
  const initializeNotifications = useNotificationStore(({ initialize }) => initialize);
  const resetNotifications = useNotificationStore(({ reset }) => reset);

  const [walletInfo, setWalletInfo] = useLocalStorage<WalletInfo | null>('wallet-info', null);
  const [wallet, setWallet] = useState<ProtoWallet | null>(null);
  const [walletType, setWalletType] = useState<WalletType | null>(null);
  const [accountRevision, setAccountRevision] = useState(0);

  // Callback system for wallet events
  const connectCallback = useRef<VoidFunction | null>(null);
  const disconnectCallback = useRef<VoidFunction | null>(null);

  // Computed wallet props
  // const walletAddress = wallet?.account.funds.address || null;
  // const pubkey = wallet?.account.funds.pubkey || null;
  const walletAddress = wallet?.account.funds.address || null;
  const pubkey = wallet?.account.funds.pubkey || null;

  // ref to store current vault pubkey for detecting real account changes
  // note: vault pubkey stays the same when address type changes, but changes when account changes
  const currentVaultPubkeyRef = useRef<string | null>(null);

  const removeListenerAccountChange = useRef<FunctionVoid | null>(null);
  const removeListenerNetworkChange = useRef<FunctionVoid | null>(null);
  const subscriptionGeneration = useRef(0);
  const accountRevisionRef = useRef(0);
  const accountSnapshotRef = useRef<WalletAccountSnapshot | null>(null);
  const publicationTokenRef = useRef(0);

  const clearWalletSubscriptions = useCallback(() => {
    ++subscriptionGeneration.current;
    const removers = [removeListenerAccountChange.current, removeListenerNetworkChange.current];
    removeListenerAccountChange.current = null;
    removeListenerNetworkChange.current = null;
    for (const remove of removers) {
      try {
        remove?.();
      } catch {}
    }
  }, []);
  useEffect(() => clearWalletSubscriptions, [clearWalletSubscriptions]);

  // Todo: move this completely out into AppStore.
  // const config = useMemo(() => {
  //   console.log(`[useMemo] selectedNetwork=${selectedNetwork}`);
  //   return selectedNetwork && getConfig(selectedNetwork);
  // }, [selectedNetwork]);

  const { data: contract, isFetching: isContractLoading, error: contractError, refetch: refetchContract } = useContractProfile(config);
  const retryContract = async () => {
    const result = await refetchContract();
    return !!result.data && !result.error;
  };
  const terms = useMemo(() => {
    if (contract) {
      const terms = get_vault_terms(contract.proto_terms);

      return terms;
    }
  }, [contract]);

  const advanceAccountRevision = () => {
    // Move the ref first so guards reject synchronously before React publishes the new authority.
    accountRevisionRef.current += 1;
    setAccountRevision(accountRevisionRef.current);
  };

  const beginAccountPublication = (invalidate: boolean): AccountPublication => {
    const token = ++publicationTokenRef.current;
    const invalidated = invalidate && accountSnapshotRef.current !== null;
    if (invalidated) {
      accountSnapshotRef.current = null;
      advanceAccountRevision();
    }
    return { invalidated, revision: accountRevisionRef.current, token };
  }; // A newer attempt advances the token so late connect, reconnect, or event completions cannot republish.

  const publicationIsCurrent = (publication: AccountPublication) =>
    publication.token === publicationTokenRef.current && publication.revision === accountRevisionRef.current;

  // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO: check array dependencies>
  useEffect(() => {
    // If walletInfo is in LS and wallet does not exist we assume user reloaded browser and we want to auto reconnect wallet extension.
    if (!wallet && walletInfo && contract && config) {
      if (publicationTokenRef.current !== 0) return;
      const publication = beginAccountPublication(false);
      // console.log('AUTO RECONNECT WALLET', walletInfo);
      reconnectWallet({ walletInfo, config })
        .then((connection) => onConnectWalletAccount({ ...connection, publication }))
        .catch(() => {
          /* A failed automatic reconnect leaves the wallet disconnected. */
        });
    }
  }, [wallet, walletInfo, contract, config]);

  const [connectedNetwork, setConnectedNetwork] = useState<ChainNetwork | 'unknown' | null>(null);

  // Reset only when the selected network changes. Configuration also changes
  // with network settings, which should not reset the wallet session here.
  const previousNetworkRef = useRef<ChainNetwork | undefined>(selectedNetwork);
  // biome-ignore lint/correctness/useExhaustiveDependencies: publication authority is held in monotonic refs
  useEffect(() => {
    if (previousNetworkRef.current !== selectedNetwork) {
      beginAccountPublication(true);
      clearWalletSubscriptions();
      resetActionTx();
      setWallet(null);
      setWalletType(null);
      previousNetworkRef.current = selectedNetwork;
    }
  }, [selectedNetwork, resetActionTx, clearWalletSubscriptions]);

  const handleNetworkChange = async (network: ChainNetwork) => {
    if (network === selectedNetwork) {
      return;
    }

    // If user is connected and selects a different network then we disconnect his current wallet.
    disconnect();
  };

  const signMessage = async (options: SignMessageOptions) => {
    if (!walletType) {
      throw new Error(t('errors.wallet.typeRequiredForSigning'));
    }

    const walletConnector = getWalletConnector(walletType);

    return walletConnector.signMessage(options);
  };

  const connect = async (selectedWalletType: WalletType) => {
    if (!contract || !config || !selectedNetwork) {
      customToast.error(t('errors.wallet.noContract'));
      return false;
    }
    // Stop Quanta observers before revision invalidation can compare a saved
    // live Xverse proof against only the app-derived account snapshot.
    if (selectedWalletType === WalletType.Xverse && useAppStore.getState().quantaHeaders) {
      clearWalletSubscriptions();
      setWallet(null);
      setWalletType(null);
    }
    // A cancelled MetaMask prompt must leave the legacy Quanta session usable.
    // Publish (and invalidate its authority) only after the replacement connects.
    const retainLegacyConnection =
      selectedWalletType === WalletType.Metamask &&
      (accountSnapshotRef.current?.walletType === WalletType.Xverse || accountSnapshotRef.current?.walletType === WalletType.Unisat);
    const publication = beginAccountPublication(!retainLegacyConnection);
    setWalletErrorMessage('');

    try {
      const signetConfig = getConfig(selectedNetwork);
      if (!signetConfig) {
        throw new Error(t('errors.wallet.noNetworkConfigFor', { network: selectedNetwork }));
      }

      // Ask MetaMask from the explicit click before downloading the wallet SDK.
      // Hand off the connected provider so loading the connector cannot prompt twice.
      const connectedMetaMaskProvider = selectedWalletType === WalletType.Metamask ? await requestSnapConnection() : undefined;
      if (!publicationIsCurrent(publication)) return false;
      await loadWalletConnector(selectedWalletType);
      if (!publicationIsCurrent(publication)) return false;

      let connection: Awaited<ReturnType<typeof connectWallet>>;
      if (selectedWalletType === WalletType.Metamask) {
        connection = await connectWallet({ config, walletType: selectedWalletType, explicitUserAction: true, connectedMetaMaskProvider });
      } else {
        const connector = getWalletConnector(selectedWalletType);

        // Connect first: Xverse's wallet_changeNetwork requires existing account
        // permissions. Then switch and re-read accounts from the selected network.
        const initial = await connectWallet({ config: signetConfig, walletType: selectedWalletType, explicitUserAction: true });
        if (!publicationIsCurrent(publication)) return false;
        if (initial.errorMessageKey) {
          await onConnectWalletAccount({
            wallet: initial.wallet,
            walletType: selectedWalletType,
            errorMessageKey: initial.errorMessageKey,
            publication,
          });
          return false;
        }

        const switched = await connector.switchNetwork(selectedNetwork);
        if (!publicationIsCurrent(publication)) return false;
        if (switched === false) {
          customToast.error(t('errors.wallet.switchNetworkAndReconnect', { network: capitalize(selectedNetwork) }));
          disconnect();
          return false;
        }

        connection = await connectWallet({ config: signetConfig, walletType: selectedWalletType });
      }

      if (!publicationIsCurrent(publication)) return false;
      const { wallet, errorMessageKey } = connection;
      if (retainLegacyConnection && !wallet) {
        setWalletErrorMessage(errorMessageKey ? t(errorMessageKey) : t('errors.wallet.somethingWentWrong'));
        return false;
      }
      if (!(await onConnectWalletAccount({ wallet, walletType: selectedWalletType, errorMessageKey, publication }))) return false;

      // First publication can advance its revision. Capture that published
      // revision while retaining this attempt token for the result.
      const publishedRevision = accountRevisionRef.current;
      if (publication.token !== publicationTokenRef.current) return false;

      // Trigger connect callback
      if (connectCallback.current) {
        try {
          connectCallback.current();
        } catch {}
      }
      return publication.token === publicationTokenRef.current && publishedRevision === accountRevisionRef.current;
    } catch (err) {
      if (!publicationIsCurrent(publication)) return false;
      const message =
        err instanceof WalletAccessDeniedError
          ? t(err.stage === 'snap-connection' ? 'errors.wallet.snapAccessDenied' : 'errors.wallet.accountAccessDenied')
          : err instanceof WalletConnectionPendingError
            ? t('errors.wallet.requestPending')
            : (err as Error).message || (err as string);
      setWalletErrorMessage(message);
      customToast.error(message);

      if (!retainLegacyConnection) disconnect(selectedWalletType);
      return false;
    }
  };

  /**
   * Handles wallet accountChange event from the wallet extension.
   * This event is triggered when:
   * 1. User changes address type (Native SegWit ↔ Nested SegWit) - reconnect with new address
   * 2. User switches account in wallet (Account 1 → Account 2) - disconnect
   *
   * We distinguish between cases by checking vault pubkey:
   * - vault pubkey stays the same when only address type changes
   * - vault pubkey changes when account changes
   */
  const handleAccountChangeEvent = async (currentWalletType: WalletType) => {
    const previousSnapshot = accountSnapshotRef.current;
    if (!previousSnapshot || previousSnapshot.walletType !== currentWalletType) return;
    const publication = beginAccountPublication(true);
    try {
      if (!contract || !config || !selectedNetwork || !previousSnapshot) {
        disconnect(currentWalletType, publication.token);

        return;
      }

      await wait(100); // small delay to let wallet extension update its state
      const connector = getWalletConnector(currentWalletType);
      const { account } = await connector.readCurrentAccount();
      if (!publicationIsCurrent(publication)) return;
      const [newWallet, { detectNetworkFromAddress }] = await Promise.all([
        configureProtoWallet({ accounts: account, config, walletConnector: connector }),
        import('@/lib/networkUtils'),
      ]);
      if (!publicationIsCurrent(publication)) return;
      const newVaultPubkey = newWallet.account.vault.pubkey;
      const isRealAccountChange = newVaultPubkey !== previousSnapshot.vault.pubkey;

      if (isRealAccountChange) {
        // real account change (Account 1 → Account 2) - disconnect
        disconnect(currentWalletType, publication.token);

        return;
      }

      // address type changed (Native SegWit ↔ Nested SegWit) - update wallet state
      const newWalletAddress = newWallet.account.funds.address;
      const newWalletPubkey = newWallet.account.funds.pubkey;
      const newRuneAddress = newWallet.account.asset.address;

      accountSnapshotRef.current = createWalletAccountSnapshot({
        account: newWallet.account,
        network: selectedNetwork,
        walletType: currentWalletType,
      });
      setWallet(newWallet);
      setWalletInfo({
        type: currentWalletType,
        address: newWalletAddress,
        publicKey: newWalletPubkey,
        network: selectedNetwork,
      });
      setConnectedNetwork(detectNetworkFromAddress(newWalletAddress));

      // update vault pubkey ref
      currentVaultPubkeyRef.current = newVaultPubkey;

      // re-initialize notifications with new addresses
      initializeNotifications(newVaultPubkey, newRuneAddress, { allowVaultHistory: currentWalletType === WalletType.Metamask });

      // update PostHog identity
      identifyPosthogUserByWalletAddress(newWalletAddress, newWalletPubkey, currentWalletType, selectedNetwork);

      // address-type change alters walletAddress; old signed headers are now
      // stale — clear them so the next login re-signs for the new address.
      setQuantaHeaders(undefined);

      // reset queries to fetch fresh data for new address
      resetWalletQueries();
      resetQuantaQueries();
    } catch {
      if (!publicationIsCurrent(publication)) return;

      disconnect(currentWalletType, publication.token);
    }
  };

  /**
   * Handles the wallet's networkChange event.
   *
   * Wallets (e.g. UniSat) do NOT disconnect when the network is switched, so we
   * must not blindly disconnect either. The app runs on signet; we only care that
   * the wallet is not on MAINNET. Re-read the wallet's current address and:
   *   - if it's a mainnet address, disconnect (the app doesn't serve mainnet here);
   *   - otherwise (signet / testnet family) stay connected and refresh state.
   */
  const handleNetworkChangeEvent = async (currentWalletType: WalletType, _isMainnet: boolean, _walletNetwork: string) => {
    const previousSnapshot = accountSnapshotRef.current;
    if (!previousSnapshot || previousSnapshot.walletType !== currentWalletType) return;
    const publication = beginAccountPublication(true);
    try {
      if (!contract || !config || !selectedNetwork || !previousSnapshot) {
        return disconnect(currentWalletType, publication.token);
      }

      await wait(100); // let the wallet extension settle after the network switch
      const { account, observedNetwork } = await getWalletConnector(currentWalletType).readCurrentAccount();
      if (!publicationIsCurrent(publication)) return;
      const currentWallet = await configureProtoWallet({
        accounts: account,
        config,
        walletConnector: getWalletConnector(currentWalletType),
      });
      if (!publicationIsCurrent(publication)) return;
      const currentSnapshot = createWalletAccountSnapshot({
        account: currentWallet.account,
        network: selectedNetwork,
        walletType: currentWalletType,
      });
      if (
        !isObservedWalletNetworkCompatible(selectedNetwork, observedNetwork) ||
        !walletAccountSnapshotsEqual(previousSnapshot, currentSnapshot)
      ) {
        customToast.error(t('errors.wallet.switchNetworkAndReconnect', { network: capitalize(selectedNetwork) }));
        return disconnect(currentWalletType, publication.token);
      }
      accountSnapshotRef.current = previousSnapshot;
    } catch {
      if (!publicationIsCurrent(publication)) return;

      disconnect(currentWalletType, publication.token);
    }
  };

  const onConnectWalletAccount = async ({
    wallet,
    walletType,
    errorMessageKey,
    publication,
  }: OnConnectWalletAccountParams): Promise<boolean> => {
    if (!publicationIsCurrent(publication)) return false;
    if (!wallet || !selectedNetwork) {
      setWalletErrorMessage(errorMessageKey ? t(errorMessageKey) : t('errors.wallet.somethingWentWrong'));
      disconnect(walletType, publication.token);
      return false;
    }

    const { detectNetworkFromAddress } = await import('@/lib/networkUtils');
    if (!publicationIsCurrent(publication)) return false;
    const walletConnector = getWalletConnector(walletType);
    const walletAddress = wallet.account.funds.address;
    const walletPubkey = wallet.account.funds.pubkey;
    const runeAddress = wallet.account.asset.address;
    const vaultPubkey = wallet.account.vault.pubkey;
    const snapshot = createWalletAccountSnapshot({ account: wallet.account, network: selectedNetwork, walletType });
    if (walletAccountSnapshotsEqual(accountSnapshotRef.current, snapshot)) return false;
    const storedHeaders = useAppStore.getState().quantaHeaders;
    let liveIdentities: Awaited<ReturnType<typeof getActiveWalletIdentities>> = null;
    if (
      walletType === WalletType.Xverse &&
      storedHeaders &&
      !quantaHeadersBelongToWallet(storedHeaders, wallet, accountRevisionRef.current)
    ) {
      // Xverse may expose a nested payment address while the app derives a
      // native address from the same key. Verify the live roles before deciding
      // whether to retire a saved proof; otherwise reconnect can select a
      // different vault account instead of preserving the existing login.
      clearWalletSubscriptions();
      setWallet(null);
      setWalletType(null);
      try {
        const { getActiveWalletIdentities } = await import('@/hooks/api/quanta/auth');
        liveIdentities = await getActiveWalletIdentities(walletType, wallet);
      } catch {
        // An unavailable provider cannot establish that a saved proof is stale.
      }
      if (!publicationIsCurrent(publication) || useAppStore.getState().quantaHeaders !== storedHeaders) return false;
      if (!liveIdentities) {
        const message = t('errors.wallet.activeAccountUnavailable');
        setWalletErrorMessage(message);
        customToast.error(message);
        return false;
      }
    }
    if (!publication.invalidated) advanceAccountRevision();
    if (!publicationIsCurrent({ ...publication, revision: accountRevisionRef.current })) return false;
    // Retire app listeners without revoking the source wallet's connection: Quanta
    // migration still needs it to sign alongside the newly connected MetaMask.
    clearWalletSubscriptions();
    accountSnapshotRef.current = snapshot;

    if (liveIdentities) rememberQuantaIdentities(wallet, accountRevisionRef.current, liveIdentities);
    if (storedHeaders && !quantaHeadersBelongToWallet(storedHeaders, wallet, accountRevisionRef.current)) {
      setQuantaHeaders(undefined);
      resetQuantaQueries();
    }
    setWalletType(walletType);
    setWallet(wallet);
    setWalletInfo({
      type: walletType,
      address: walletAddress,
      publicKey: walletPubkey,
      network: selectedNetwork,
    });
    setConnectedNetwork(detectNetworkFromAddress(walletAddress));

    // store vault pubkey for detecting real account changes (vault pubkey stays same when only address type changes)
    currentVaultPubkeyRef.current = vaultPubkey;

    initializeNotifications(vaultPubkey, runeAddress, { allowVaultHistory: walletType === WalletType.Metamask });

    identifyPosthogUserByWalletAddress(walletAddress, walletPubkey, walletType, selectedNetwork);

    // subscribe to account and network change: accountChange is called when: account changes (Account 1 → Account 2) OR address type changes (Nested SegWit ↔ Native SegWit)
    const generation = subscriptionGeneration.current;
    removeListenerAccountChange.current = walletConnector.subscribe.accountChange(() => {
      if (generation !== subscriptionGeneration.current) return;
      handleAccountChangeEvent(walletType);
    });
    // networkChange fires when the wallet's network changes. Wallets don't
    // disconnect on a switch, so we don't either — we only disconnect if the wallet
    // moved to mainnet (see handleNetworkChangeEvent). Otherwise the session stays.
    removeListenerNetworkChange.current = walletConnector.subscribe.networkChange((event: any) => {
      if (generation !== subscriptionGeneration.current) return;
      // todo: move detection logic into connectors.
      if (event?.bitcoin?.name) {
        // Xverse:
        const walletNetwork = event?.bitcoin?.name;
        const isMainnet = walletNetwork === 'Mainnet';
        handleNetworkChangeEvent(walletType, isMainnet, walletNetwork);
      } else {
        // Unisat:
        const walletNetwork = event as string;
        const isMainnet = walletNetwork === 'livenet';
        handleNetworkChangeEvent(walletType, isMainnet, walletNetwork);
      }
    });
    return true;
  };

  const disconnect = async (wallet_type?: WalletType, expectedToken?: number) => {
    if (expectedToken !== undefined && expectedToken !== publicationTokenRef.current) return;
    ++publicationTokenRef.current;
    clearWalletSubscriptions();
    if (accountSnapshotRef.current) {
      accountSnapshotRef.current = null;
      advanceAccountRevision();
    }

    try {
      getWalletConnector(wallet_type || walletType || ('xverse' as WalletType))?.disconnect();
    } catch {}

    // reset PostHog identity on wallet disconnect
    try {
      posthog.reset();
    } catch {}

    // set the wallet, walletAddress, and store to defaults
    resetActionTx();
    resetNotifications();
    // clear persisted quanta auth headers so a future wallet never reuses them
    setQuantaHeaders(undefined);
    setWalletInfo(null);
    setWallet(null);
    setWalletType(null);
    setConnectedNetwork(null);
    clearDefaultWalletProvider();
    setResetDrawers(true);

    await wait(0);

    resetWalletQueries();
    resetQuantaQueries();

    // reset refs
    currentVaultPubkeyRef.current = null;

    // Trigger disconnect callback
    if (disconnectCallback.current) {
      try {
        disconnectCallback.current();
      } catch {}
    }
  };

  const createWalletActionGuard = (): WalletActionGuard =>
    createCurrentWalletActionGuard({
      config,
      snapshot: accountSnapshotRef.current,
      revision: accountRevisionRef.current,
      getRevision: () => accountRevisionRef.current,
    });

  // Wallet event subscription functions
  const subscribeToWalletConnect = (callback: VoidFunction) => {
    connectCallback.current = callback;

    return () => {
      connectCallback.current = null;
    };
  };

  const subscribeToWalletDisconnect = (callback: VoidFunction) => {
    disconnectCallback.current = callback;

    return () => {
      disconnectCallback.current = null;
    };
  };

  /** FIXME:
   * Performance Issue: Context Value Recreation
   *
   * Problem:
   * The context value object is recreated on every render, causing unnecessary re-renders
   * of all components consuming this context. This happens because we're creating a new
   * object in the JSX, which has a new reference each time.
   *
   * Impact:
   * - Poor performance due to unnecessary re-renders
   * - Components using useDucatClient() will re-render even if their specific
   *   dependencies haven't changed
   *
   * Solution:
   * Wrap the context value in useMemo to maintain referential equality between renders
   * when the underlying values haven't changed.
   */

  // console.log(`>>> [DucatClientProvider] <<< RERENDERED`);

  return (
    <context.Provider
      value={{
        client: {
          connect,
          disconnect,
          signMessage,
          subscribeToWalletConnect,
          subscribeToWalletDisconnect,
        },
        contract,
        terms,
        isContractLoading,
        contractError,
        retryContract,
        isConnected: wallet !== null,
        wallet,
        walletType,
        walletAddress,
        pubkey,
        connectedNetwork,
        accountRevision,
        createWalletActionGuard,
        handleNetworkChange,
        // setNetworkSettings: handleNetworkSettingsChange,
      }}
    >
      {children}
    </context.Provider>
  );
};

export const useDucatClient = (): ClientContext => {
  const ctx = useContext(context);

  if (ctx === null) {
    throw new Error('Ducat client context is null');
  }

  return ctx;
};
